<template>
  <div>
    <!-- header content -->
    <header class="header">
      <div class="d-flex align-center header__container">
        <!-- success message -->
        <div
          class="d-flex flex-column justify-center rounded ma-3 ml-xl-0 pa-4 pl-xl-10 header__info"
        >
          <h1
            class="my-n3 psorange--text font-weight-bold"
            :class="$vuetify.breakpoint.xl ? 'font-60' : 'font-52'"
          >
            Success!
          </h1>
          <p class="ma-0 font-18 white--text">
            Your application is now complete.
          </p>
        </div>
      </div>
    </header>

    <!-- main content -->
    <section
      v-show="!isProcessingData"
      class="white rounded-lg elevation-3 mx-3 mb-3 main"
    >
      <!-- top content -->
      <section class="pa-4 pb-10 pa-md-10">
        <BaseAlert
          v-if="showGeneralError"
          color="var(--red-100)"
          class="px-4 py-3 mb-4 border-sm border-solid border-error"
        >
          <div class="d-flex">
            <div class="mr-3">
              <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
            </div>
            <div class="primary--text">
              <span class="font-weight-bold">Sorry!</span> Something went wrong.
              Please try again.
            </div>
          </div>
        </BaseAlert>

        <BaseAlert
          color="var(--green-100)"
          class="px-4 py-3 mb-4 border-sm border-solid border-success"
          style="padding-top: 10px"
        >
          <div class="d-flex">
            <div class="mr-3">
              <v-icon color="var(--green-500)">mdi-check-circle</v-icon>
            </div>
            <div class="primary--text">
              <span class="font-weight-bold"
                >Application completed successfully!&nbsp;</span
              >
              Please find your application reference below.
            </div>
          </div>
        </BaseAlert>

        <BaseAlert
          v-if="
            isAdvisedSeller &&
            showSdnSection &&
            !hideSdnExternal &&
            canCreateSdn &&
            !isSdnCreated
          "
          color="var(--orange-100)"
          class="px-4 py-3 mb-4 border-sm border-solid border-warning"
        >
          <div class="d-flex">
            <div class="mr-3">
              <v-icon color="var(--orange-500)">mdi-alert</v-icon>
            </div>
            <div class="primary--text">
              <span class="font-weight-bold">Important, before you go.</span>
              Please create a Statement of Demands &amp; Needs.
              <a href="#" @click.prevent="showSdnDrawer"
                ><u>Create one now.</u></a
              >
            </div>
          </div>
        </BaseAlert>

        <div class="mt-10">
          <p class="ma-0 mb-1 font-18 font-weight-medium primary--text">
            Application reference
          </p>
          <p class="ma-0 font-30 font-weight-bold accent--text text-uppercase">
            {{ getApplicationReference ?? quoteReference }}
          </p>
        </div>

        <BaseAlert
          v-if="isAdvisedSeller"
          color="var(--blue-100)"
          class="pa-4 mt-5 mb-0 border-sm border-solid border-info"
        >
          <div class="d-flex">
            <div class="mr-3">
              <v-icon color="var(--blue-500)">mdi-information</v-icon>
            </div>
            <div class="primary--text">
              <span class="font-weight-bold">Important!&nbsp;</span>
              Your premium and cover are based on the information we now hold
              for you and your home, so it’s important that, if at any time, you
              want to change any of the information you have previously given,
              or your circumstances change you tell us. If you don’t it may
              affect your policy cover, could lead to a potential claim not
              being paid and could also lead to cancellation of your policy.
            </div>
          </div>
        </BaseAlert>

        <!-- summary card -->
        <LayoutApplicationSummaryCard
          @showPremiumBreakdownMonthly="showPremiumBreakdownMonthlyCustomTitle"
          @showPremiumBreakdown3Months="showPremiumBreakdown3Months"
          @showPremiumBreakdownAnnual="showPremiumBreakdownAnnual"
          @showPremiumBreakdown2Months="showPremiumBreakdown2MonthsFree"
          @shareQuote="shareQuote"
          @printQuote="printQuote"
          :productGroup="productGroup"
          :showPayByCreditDebitCardMessage="false"
          :cardTitle="cardTitle"
          :monthlyTitle="monthlyTitle"
          :annualTitle="annualTitle"
          :useRetrievedPaymentInfo="
            useRetrievedPaymentInfo !== null
              ? useRetrievedPaymentInfo
              : $route.params.type === 'resumeQuote'
          "
        />

        <!-- TC policy terms content -->
        <section v-if="isTenantsJourney" class="policy-details pt-10">
          <p>
            The policy has a 14-day cooling off period from the start date of
            the policy or when you receive your policy documents, whichever is
            later. You have the right to cancel your policy within the 14 day
            cooling off period. If you cancel within this period any premiums
            paid will be refunded providing you haven’t made a claim. After the
            14 day cooling off period, you will still have the right to cancel
            your policy, however you will be charged for the cost of your cover
            on a pro rata daily basis.
          </p>

          <p>
            The date of the first payment, will be confirmed on your policy
            schedule
          </p>

          <p>
            Please read through the policy documents to make sure that the cover
            is suitable for your needs.
          </p>

          <p>
            If you have chosen to receive your documents by email, you’ll
            receive an email shortly from noreply@paymentshield.co.uk asking you
            to activate your online portal and you can access your documentation
            there.
          </p>

          <p>
            If you have asked for your documents to be sent in the post, you
            will receive the policy documents to the insured or correspondence
            address you have selected in 5-7 working days.
          </p>
        </section>

        <!-- SDN content -->
        <section v-if="showSdnSection && !hideSdnExternal" class="mt-12">
          <p class="mb-3 font-20 font-weight-bold">
            Statement of demands and needs
          </p>

          <div
            v-if="canCreateSdn && !isSdnCreated && !isQuilterFactFind"
            class="d-flex flex-column justify-center flex-xl-row justify-xl-space-between align-xl-center"
          >
            <p class="ma-0 px-0 pb-6 pl-xl-4 pr-xl-16 pb-xl-0">
              {{ createStatementOfDemandsAndNeedsDescription }}
            </p>

            <BaseButton
              dataUiid=""
              depressed
              rounded
              outlined
              color="webgrey"
              text="CREATE SDN"
              textColor="primary--text"
              height="60"
              className="font-20"
              :width="$vuetify.breakpoint.xlOnly ? '256px' : '100%'"
              @click="showSdnDrawer"
            />
          </div>

          <div
            v-else-if="canCreateSdn && isStandardSdnCreated"
            class="d-flex flex-column justify-center flex-xl-row justify-xl-space-between align-xl-center"
          >
            <v-icon color="#1d8649" class="d-none d-xl-inline-flex"
              >mdi-check-circle</v-icon
            >
            <p class="ma-0 px-0 pb-6 pl-xl-4 pr-xl-16 pb-xl-0">
              You can now download the statement of demands and needs from the
              documents section below.
            </p>

            <BaseButton
              dataUiid=""
              depressed
              rounded
              outlined
              color="webgrey"
              text="RECREATE SDN"
              textColor="primary--text"
              height="60"
              className="font-20"
              :width="$vuetify.breakpoint.xlOnly ? '256px' : '100%'"
              @click="showSdnDrawer"
            />
          </div>

          <div
            v-else
            class="d-flex flex-column justify-center flex-xl-row align-xl-center justify-xl-start"
          >
            <v-icon color="#1d8649" class="d-none d-xl-inline-flex"
              >mdi-check-circle</v-icon
            >
            <p class="ma-0 px-0 pl-xl-4 pb-xl-0">
              You can now download the statement of demands and needs from the
              documents section below.
            </p>
          </div>
        </section>

        <!-- documents content -->
        <section class="mt-11">
          <p class="mb-3 font-20 font-weight-bold">Document selection</p>

          <BaseAlert
            v-if="isDownloadError"
            ref="downloadError"
            color="var(--red-100)"
            class="mb-6 pa-4 ma-0 border-sm border-solid border-error"
          >
            <div class="d-flex">
              <div class="mr-3">
                <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
              </div>
              <div>
                <span class="font-weight-bold">Sorry!&nbsp;</span>
                Document could not be downloaded at this time.
              </div>
            </div>
          </BaseAlert>

          <!-- documents grid -->
          <section class="main__document-container">
            <v-overlay absolute :value="isShowDocumentsOverlay">
              <v-progress-circular
                indeterminate
                size="64"
              ></v-progress-circular>
            </v-overlay>

            <v-row class="ma-0 mt-n10">
              <v-col
                cols="12"
                lg="6"
                class="pt-10 pb-0 px-5"
                v-for="file in visibleFiles"
                :key="file.key"
              >
                <!-- document card -->
                <v-card
                  elevation="0"
                  class="d-flex flex-column justify-space-between pa-6 pa-xl-10 rounded-lg"
                  :color="
                    file.isDownloaded ? 'bg-blue-grey-500' : 'bg-blue-grey-900'
                  "
                  :height="360"
                >
                  <!-- document card top content -->
                  <div>
                    <div class="d-flex justify-end">
                      <div class="document-card__icon">
                        <v-icon
                          v-if="!file.isDownloaded"
                          class="document-card__icon--active"
                          color="red"
                          size="12"
                          >mdi-circle</v-icon
                        >
                        <v-icon color="white" size="33">mdi-file</v-icon>
                      </div>
                    </div>

                    <p class="font-24 font-weight-medium white--text my-4">
                      {{ file.title }}
                    </p>

                    <p class="font-14 font-weight-medium white--text mb-0">
                      PDF
                    </p>
                  </div>

                  <!-- document card bottom content -->
                  <div class="pt-10 pb-4 pt-xl-10 pb-xl-0 px-xl-4">
                    <BaseButton
                      text="DOWNLOAD"
                      height="48"
                      depressed
                      rounded
                      block
                      loadingText="DOWNLOADING..."
                      fontSize="font-20"
                      className="font-weight-bold"
                      :outlined="file.isDownloaded"
                      :color="file.isDownloaded ? 'white' : 'psorange'"
                      :loading="file.isDownloading"
                      :loadingIconColor="
                        file.isDownloaded && file.isDownloading ? 'white' : ''
                      "
                      @click="downloadFile(file.key)"
                    />
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </section>
        </section>
      </section>

      <v-divider />

      <!-- bottom content -->
      <section class="px-4 py-10 pa-xl-10">
        <p class="ma-0 mt-n1 font-18 font-weight-bold">Questions?</p>
        <p class="mb-12">
          You can check out our
          <a
            title="frequently asked questions"
            href="https://paymentshield.helpshift.com/hc/en/4-advisers/"
            target="_blank"
            class="text-decoration-underline accent--text"
            aria-label="frequently asked questions"
            >frequently asked questions
            <v-icon size="medium">mdi-open-in-new</v-icon></a
          >
          for more information.
        </p>

        <p class="ma-0 font-18 font-weight-bold">Next steps</p>
        <p>
          If you need to make a claim or cancel your policy, simply follow the
          procedure on your insurer schedule or contact our customer services
          team.
        </p>
        <p class="ma-0">
          We recommend you keep all information relating to your policy in a
          safe place to make sure you have all the relevant details if you need
          to contact us.
        </p>
      </section>
    </section>

    <v-overlay :value="overlay || !getApplicationReference">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- premium breakdown dialog -->
    <BaseDialog
      ref="DialogContentPremiumBreakdown"
      windowClass="pa-0 pa-xl-4 pb-xl-0"
      headerClass="pa-4"
    >
      <template v-slot:content="{ data }">
        <slot
          name="dialogContentPremiumBreakdown"
          v-if="dialogContentPremiumBreakdownSlot"
          :breakdownModel="data"
        ></slot>
        <DialogContentPremiumBreakdown
          v-else
          :insurancePremiumTitle="insurancePremiumTitle"
          :breakdownModel="data"
          :showTotalPremiumIPT="showTotalPremiumIPT"
          :cancellingYourPolicyDescription="cancellingYourPolicyDescription"
        ></DialogContentPremiumBreakdown>
      </template>
    </BaseDialog>

    <!-- share dialog -->
    <BaseDialog
      ref="shareDialog"
      headerIcon="mdi-share-variant"
      headerIconColor="#106175"
    >
      <template v-slot:content="{ data }">
        <DialogContentShareQuotes
          :content="content"
          :quoteId="data"
          :showSdn="isSdnCreated"
          :showNonAdvisedSdn="isNonAdvisedSdnCreated"
          :showSuitabilityReport="isSuitabilityReportCreated"
          :showAllQuoteSummary="showAllQuoteSummary"
          :show-confirmation-of-cover="isConfirmationOfCoverCreated"
          @success="shareQuotesSuccess"
        ></DialogContentShareQuotes>
      </template>
    </BaseDialog>

    <!-- print dialog -->
    <BaseDialog
      ref="printQuotes"
      headerIcon="mdi-printer"
      headerIconColor="#106175"
      headerDivider
      windowClass="pa-0 pb-xl-0 mb-0"
      headerClass="px-7 pt-6 pb-3"
      contentClass="mb-0"
    >
      <template v-slot:content="{ data }">
        <DialogContentPrintQuotes :data="data"></DialogContentPrintQuotes>
      </template>
    </BaseDialog>

    <!-- SDN drawer (advised) -->
    <template v-if="isAdvisedSeller">
      <DrawerDemandsAndNeedsAdvised
        v-if="sdnDataAdvised"
        v-model="isShowDrawerDemandsAndNeedsAdvised"
        :sdnData="sdnDataAdvised"
        @refreshDocs="getQuoteDocuments"
      ></DrawerDemandsAndNeedsAdvised>
    </template>
    <template v-else>
      <!-- SDN drawer (default) -->
      <DrawerDemandsAndNeeds
        v-if="sdnData"
        v-model="isShowDrawerDemandsAndNeeds"
        :sdnData="sdnData"
        @refreshDocs="getQuoteDocuments"
      ></DrawerDemandsAndNeeds>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import {
  APPLICATION_MODULE,
  QUESTIONS_MODULE,
} from "@/store/types/module-types";
import { applyThirdPartyId, downloadDocument, scrollTop } from "@/common/utils";

// LayoutApplicationSummaryCard dependencies
import { ANNUAL, PRODUCT } from "@/constants/common.constants";
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import { filterSuccessQuoteDocs } from "@/common/docs";
import { adBoolToString } from "@/common/cover";
import {
  addSuccessPageSortOrderToDocs,
  sortQuoteDocsBySortOrder,
} from "@/common/sorting";
import documentService from "@/services/document.service";
import { getProductGroup } from "@/common/question-set";

import BaseAlert from "@/components/BaseComponents/BaseAlert.vue";
import BaseDialog from "@/components/BaseComponents/BaseDialog.vue";
import BaseButton from "@/components/BaseComponents/BaseButton.vue";

// LayoutApplicationSummaryCard components
import LayoutApplicationSummaryCard from "@/components/LayoutComponents/LayoutApplicationSummaryCard.vue";
import DialogContentPremiumBreakdown from "@/components/QuoteResult/DialogContentPremiumBreakdown.vue";
import DialogContentShareQuotes from "@/components/QuoteResult/DialogContentShareQuotes.vue";
import DialogContentPrintQuotes from "@/components/QuoteResult/DialogContentPrintQuotes.vue";

// SDN components
import DrawerDemandsAndNeeds from "@/components/QuoteResult/DrawerDemandsAndNeeds.vue";
import DrawerDemandsAndNeedsAdvised from "@/components/QuoteResult/DrawerDemandsAndNeedsAdvised.vue";
import { getNamePageSuccessByJourney } from "@/common/navigating";

export default {
  name: "CompletePage",
  components: {
    BaseAlert,
    BaseDialog,
    BaseButton,

    LayoutApplicationSummaryCard,
    DialogContentPremiumBreakdown,
    DialogContentShareQuotes,
    DialogContentPrintQuotes,

    DrawerDemandsAndNeeds,
    DrawerDemandsAndNeedsAdvised,
  },
  mixins: [PremiumBreakdownMixin],
  props: {
    sdnData: {
      type: Array,
      required: false,
    },
    cardTitle: {
      type: String,
    },
    monthlyTitle: {
      type: String,
    },
    annualTitle: {
      type: String,
    },
    sdnDataAdvised: {
      type: Array,
      required: false,
    },
    hideSdnExternal: {
      type: Boolean,
      required: false,
    },
    content: {
      type: Object,
      required: true,
    },
    showAllQuoteSummary: {
      type: Boolean,
      default: true,
    },
    showTotalPremiumIPT: {
      type: Boolean,
      default: false,
    },
    insurancePremiumTitle: {
      type: String,
      required: false,
    },
    premiumBreakdownMonthlyTitle: {
      type: String,
      required: false,
      default: "Premium Breakdown",
    },
    cancellingYourPolicyDescription: {
      type: String,
      default:
        "You have the right to cancel your policy within the 14 day cooling off period. After the 14 day cooling off period, you will still have the right to cancel your policy, however you will be charged for the cost of your cover on a pro rata daily basis. If you have suffered a claim and you wish to cancel your policy you will be required to pay for your policy in full.",
    },
    createStatementOfDemandsAndNeedsDescription: {
      type: String,
      default:
        "As your sale has been completed on an ‘Advised’ basis, please create a Statement of Demands & Needs document to be provided to your client.",
    },
    useRetrievedPaymentInfo: {
      type: Boolean,
      default: null,
    },
    dialogContentPremiumBreakdownSlot: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isProcessingData: true,

      // LayoutApplicationSummaryCard dependencies
      quoteDocuments: [],
      files: [
        {
          title: "Suitability report",
          key: "suitability",
          isVisible: false,
          isDownloaded: false,
          isDownloading: false,
        },
        {
          title: "Quote and Application Summary",
          key: "qas",
          isVisible: true,
          isDownloaded: false,
          isDownloading: false,
        },
        {
          title: "Confirmation of Cover",
          key: "cofc",
          isVisible: false,
          isDownloaded: false,
          isDownloading: false,
        },
        {
          title: "Statement of Demands and Needs",
          key: "sdn",
          isVisible: false,
          isDownloaded: false,
          isDownloading: false,
        },
        {
          title: "Statement of Demands and Needs",
          key: "factfindsdn",
          isVisible: false,
          isDownloaded: false,
          isDownloading: false,
        },
        {
          title: "Statement of Demands and Needs",
          key: "sdnnonadvised", // MPPI Non Advised SDN
          isVisible: false,
          isDownloaded: false,
          isDownloading: false,
        },
      ],
      documentList: [],
      overlay: false,

      // SDN dependencies
      isShowDrawerDemandsAndNeeds: false,
      isShowDrawerDemandsAndNeedsAdvised: false,

      // Document dependencies
      isShowDocumentsOverlay: false,
      isDownloadError: false,

      showGeneralError: false,
    };
  },
  computed: {
    ...mapGetters(APPLICATION_MODULE, [
      "getApplicationReference",
      "quoteId",
      "selectedQuote",
    ]),
    ...mapGetters(QUESTIONS_MODULE, [
      "quoteReference",
      "getCover",
      "getQuoteRequestId",
      "callChannel",
      "httpConfig",
      "insurerQuotesModel",
      "qsId",
      "isSelfServe",
      "productId",
      "isAdvisedSeller",
      "isQuilterFactFind",
      "isMPPI",
    ]),
    ...mapState(APPLICATION_MODULE, ["questionMap"]),
    ...mapState(QUESTIONS_MODULE, ["userId"]),
    productGroup() {
      return getProductGroup(this.qsId);
    },
    paymentFrequencyForDocs() {
      // Prevent returning an empty string for Payment Frequency if the payment method is card.
      return this.questionMap.PaymentMethod.Value === "CreditCard"
        ? ANNUAL
        : this.questionMap.PaymentFrequency.Value;
    },
    canCreateSdn() {
      const basisOfAdvice = this.questionMap.BasisOfAdvice.Value;
      return basisOfAdvice === "Advised";
    },
    showSdnSection() {
      // Quote Docs response has returned and there is no OW Suitability doc
      return (
        this.quoteDocuments.length > 0 &&
        !this.files.some((f) => f.isVisible && f.key === "suitability")
      );
    },
    isSdnCreated() {
      // Quote docs response has returned and there is an SDN
      return (
        this.quoteDocuments.length > 0 &&
        this.files.some(
          (f) => f.isVisible && (f.key === "sdn" || f.key === "factfindsdn")
        )
      );
    },
    isSuitabilityReportCreated() {
      // Quote docs response has returned and there is an Suitability Report
      return (
        this.quoteDocuments.length > 0 &&
        this.files.some((f) => f.isVisible && f.key === "suitability")
      );
    },
    isConfirmationOfCoverCreated() {
      return (
        this.quoteDocuments.length > 0 &&
        this.files.some((f) => f.isVisible && f.key === "cofc")
      );
    },
    isStandardSdnCreated() {
      return (
        this.quoteDocuments.length > 0 &&
        this.files.some((f) => f.isVisible && f.key === "sdn")
      );
    },
    isNonAdvisedSdnCreated() {
      // Quote docs response has returned and there is an MPPI Non Advised SDN
      return (
        this.quoteDocuments.length > 0 &&
        this.files.some((f) => f.isVisible && f.key === "sdnnonadvised")
      );
    },
    visibleFiles() {
      return this.files.filter((f) => f.isVisible);
    },
    isTenantsJourney() {
      return (
        this.productId === PRODUCT.Tenants_Liability ||
        this.productId === PRODUCT.Tenants_Contents
      );
    },
  },
  async beforeMount() {
    const paymentMethod = sessionStorage.getItem("paymentMethod");

    if (paymentMethod === "CreditCard") {
      await Promise.all([
        this.retrieveQuoteFollowingSuccessfulCardPayment(),
        this.repopulateActiveQuoteFromSessionStorage(),
      ]);
      sessionStorage.removeItem("paymentMethod");
      applyThirdPartyId(this.isSelfServe, this.userId);
      const routeName = getNamePageSuccessByJourney();
      if (this.isSelfServe) {
        this.$router.push({
          name: routeName,
          params: {
            type: "resumeQuote",
            quoteRequestId: this.getQuoteRequestId,
            syncQuoteRequest: true,
          },
        });
        return;
      }
    } else if (paymentMethod === "DirectDebit") {
      // remove payment method from storage so that if the page is refreshed, user is redirected to adviser hub.
      sessionStorage.removeItem("paymentMethod");
      //Params is used when retrieving a quote. If no params, redirect to adviser hub.
    } else if (!this.$route.params.type) {
      this.exitToAdviserHub("submitted_quotes");
      return;
    }
    this.isProcessingData = false;

    // get quote documents moved here to ensure state restoration when redirecting from card payment is done
    this.getQuoteDocuments();
  },
  mounted() {
    scrollTop();
    // Can't go forward automatically from this page
    this.setRestoringLastStep(false);

    // Google analytics
    window.dataLayer.push({
      event: "purchase",
      ecommerce: {
        purchase: {
          transaction_id: this.getApplicationReference ?? this.quoteReference,
          value: this.selectedQuote.AnnualTotal?.toString(),
          currency: "GBP",
          items: [
            {
              item_name: this.selectedQuote.ProductName,
              item_id: this.productId.toString(),
              price: this.selectedQuote.AnnualTotal?.toString(),
              item_brand: this.selectedQuote.InsurerName,
              quantity: "1",
            },
          ],
        },
      },
    });

    // add beforeunload listener
    if (this.isAdvisedSeller && !this.isSdnCreated) {
      window.addEventListener("beforeunload", this.handleBeforeUnload);
    }
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "setRestoringLastStep",
      "exitToAdviserHub",
    ]),
    ...mapActions(APPLICATION_MODULE, [
      "retrieveQuoteByQuoteRequestIdFollowingSuccessfulCardPayment",
      "repopulateActiveQuoteFromSessionStorage",
    ]),
    showPremiumBreakdownMonthlyCustomTitle(quote, productGroup) {
      const customTitle = this.premiumBreakdownMonthlyTitle;
      this.showPremiumBreakdownMonthly(quote, productGroup, customTitle);
    },

    async retrieveQuoteFollowingSuccessfulCardPayment() {
      await this.retrieveQuoteByQuoteRequestIdFollowingSuccessfulCardPayment();
    },

    // share dialog methods
    shareQuote(quoteId) {
      this.$refs.shareDialog.open("Share via email", quoteId);
    },
    shareQuotesSuccess() {
      this.$refs.shareDialog.cancel();
    },

    // print dialog methods
    async getDocumentList() {
      if (this.documentList.length) {
        return;
      }
      const res = await documentService.getDocumentList(
        this.getQuoteRequestId,
        this.callChannel,
        this.httpConfig
      );
      if (res.StatusCode === "OK") {
        //The code below is to have a copy of these files for each quote so we could track their download state.
        //only two records of ipid and terms are coming back from api, we need one for each quote for their download state.
        const missingQuoteIdDocs = [];

        res.Documents.forEach((doc) => {
          doc.isDownloading = false;
          doc.isDownloaded = false;
          if (
            doc.Code === "terms" ||
            doc.Code === "ipid" ||
            doc.Code === "factfindsdn"
          ) {
            missingQuoteIdDocs.push(doc);
          }
        });

        if (!missingQuoteIdDocs.length) {
          return;
        }
        this.insurerQuotesModel.forEach((quote) => {
          if (!quote.isAvailable) {
            return;
          }
          quote.quotes.forEach((insurerQuote) => {
            for (const doc of missingQuoteIdDocs) {
              res.Documents.push({
                ...doc,
                QuoteId: insurerQuote.QuoteId,
              });
            }
          });
        });
        this.documentList = res.Documents;
      }
    },
    async printQuote(quote) {
      this.overlay = true;
      try {
        this.showGeneralError = false;
        await this.getDocumentList();
        const data = {
          ...quote,
          files: this.documentList
            .filter((doc) =>
              filterSuccessQuoteDocs(
                doc,
                quote.QuoteId,
                {
                  contentsAd: adBoolToString(quote.ContentsAD),
                  buildingsAd: adBoolToString(quote.BuildingsAD),
                },
                this.paymentFrequencyForDocs
              )
            )
            .map(addSuccessPageSortOrderToDocs)
            .sort(sortQuoteDocsBySortOrder),
          type: "multi",
        };

        data.files = data.files.filter(
          (item, index, self) =>
            index ===
            self.findIndex((t) => t.Code === item.Code && t.Name === item.Name)
        );

        this.$refs.printQuotes.open("Download for printing", data);
      } catch (error) {
        console.error(error);
        this.showGeneralError = true;
        scrollTop();
      } finally {
        this.overlay = false;
      }
    },

    // SDN methods
    async getQuoteDocuments() {
      this.files.find((file) => file.key === "sdn").isDownloaded = false;
      this.documentList = [];
      this.isShowDocumentsOverlay = true;
      try {
        const res = await documentService.getQuoteDocuments(
          this.quoteId,
          this.callChannel,
          this.httpConfig
        );
        this.quoteDocuments = res.Documents;
        this.files.forEach((f) => {
          const matchedDocument = res.Documents.find(
            (doc) => doc.Code === f.key
          );
          f.isVisible = Boolean(matchedDocument);
          f.title = matchedDocument?.Name ? matchedDocument.Name : f.title;
        });

        // remove beforeunload listener
        if (this.isAdvisedSeller && this.isSdnCreated) {
          window.removeEventListener("beforeunload", this.handleBeforeUnload);
        }
      } catch (err) {
        console.log(err);
        this.isDownloadError = true;
      } finally {
        this.isShowDocumentsOverlay = false;
      }
    },

    showSdnDrawer() {
      if (this.isAdvisedSeller) {
        this.isShowDrawerDemandsAndNeedsAdvised = true;
      } else {
        this.isShowDrawerDemandsAndNeeds = true;
      }
    },

    // Document methods
    downloadFile(documentCode) {
      // `file` is the matching entry from the API response
      const file = this.quoteDocuments.find(
        (document) => document.Code === documentCode
      );

      if (!file) {
        this.isDownloadError = true;
        return;
      }

      this.isDownloadError = false;

      // `theDoc` is our local data model that holds the download status
      const theDoc = this.files.find((f) => f.key == documentCode);
      if (theDoc.isDownloading) return;
      theDoc.isDownloading = true;

      documentService
        .getDocument(file.Link, this.httpConfig)
        .then((res) => {
          downloadDocument(res, theDoc.title);
          theDoc.isDownloading = false;
          theDoc.isDownloaded = true;
        })
        .catch((error) => {
          theDoc.isDownloading = false;
          theDoc.isDownloaded = false;
          this.isDownloadError = true;
          setTimeout(() => {
            const el = this.$refs.downloadError;
            if (el) {
              el.scrollIntoView({ behavior: "smooth" });
            }
          }, 500);
          console.error(error);
        });
    },

    handleBeforeUnload(e) {
      // recommended by MDN
      e.preventDefault();
      e.returnValue = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.header {
  background-image: url(~@/assets/images/payment_success_background.jpg);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 350px;

  .header__container {
    width: 100%;
    max-width: 1072px;
    height: 100%;
    margin: 0 auto;
    padding-bottom: 125px;

    .header__info {
      width: 100%;
      max-width: 384px;
      height: 140px;
      background-color: rgba($black-2, 0.8);
    }
  }
}

.main {
  position: relative;
  z-index: 4;
  margin-top: 225px;

  .main__document-container {
    position: relative;
  }

  .document-card__icon {
    position: relative;

    .document-card__icon--active {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
    }
  }
}

@media screen and (max-width: $grid-breakpoints-lg) {
  .header {
    height: 206px;

    .header__container {
      padding-bottom: 33px;

      .header__info {
        max-width: initial;
        height: auto;
      }
    }
  }

  .main {
    margin-top: 173px;
  }
}
</style>
